import { useEffect, useState } from 'react';
import { useSelector } from '../redux/store';
import { Mixpanel } from '../common/mixpanel';
import { MixpanelChatEventName } from '../common/events';
import { getMixpanel } from '../redux/mixpanel/mixpanelSelectors';
import { getLanguage } from '../redux/localization/localizationSelectors';
import { getHash } from '../redux/form/formSelectors';
import { Logger } from '../common/logger';
import driverRegistrationApiClient from '../api/DriverRegistrationApi';

interface LiveChatProps {
  country: string | undefined;
  enabled: boolean;
  key: string | null;
}

/**
 * Documentation for Infobip Live Chat SDK:
 * https://www.infobip.com/docs/live-chat/javascript-sdk-in-live-chat
 */

enum LiveChatAction {
  AUTH = 'auth',
  CLEAR = 'clear',
  INIT = 'init',
  SEND_CONTEXTUAL_DATA = 'send_contextual_data',
  SET_LANGUAGE = 'set_language',
  SET_FONT = 'set_font',
}

const liveChatCommand = (
  action: LiveChatAction,
  data: unknown
): Promise<void> => {
  return new Promise((resolve) => {
    if (!window.liveChat) {
      Logger.log(`Live Chat: not available`);
      resolve();
      return;
    }

    Logger.log(`Live Chat: ${action} started`, { data });
    window.liveChat(action, data, (err: any) => {
      if (err) {
        Logger.log(`Live Chat: ${action} failed`, { err });
      } else {
        Logger.log(`Live Chat: ${action} success`, { data });
      }
      resolve();
    });
  });
};

const setupChatHooks = (mixpanel: Mixpanel) => {
  const chatBotState = {
    chatOpen: false,
    chatStarted: false,
  };

  const chatBotButton = document.getElementById('ib-button-messaging');
  const [chatBotCloseMobileButton] = document.getElementsByClassName(
    'ib-close-mobile-button'
  );

  for (const button of [chatBotButton, chatBotCloseMobileButton]) {
    button?.addEventListener('click', () => {
      if (chatBotState.chatOpen) {
        mixpanel.track(MixpanelChatEventName.LEAD_CHAT_OPENED);
      } else {
        mixpanel.track(MixpanelChatEventName.LEAD_CHAT_CLOSED);
      }
      chatBotState.chatOpen = !chatBotState.chatOpen;
    });
  }

  window.addEventListener('message', (e) => {
    if (e.data.analyticsEvent === 'conversation_opened') {
      if (chatBotState.chatStarted) {
        return;
      }
      chatBotState.chatStarted = true;
      mixpanel.track(MixpanelChatEventName.LEAD_CHAT_STARTED);
    }
  });
};

enum AuthState {
  NOT_AUTHENTICATED = 'not_authenticated',
  IN_PROGRESS = 'in_progress',
  AUTHENTICATED = 'authenticated',
}

export const useLiveChat = ({ country, enabled, key }: LiveChatProps): void => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState<AuthState>(
    AuthState.NOT_AUTHENTICATED
  );
  const [chatAuthToken, setChatAuthToken] = useState<string | null>(null);

  const mixpanel = useSelector(getMixpanel);
  const language = useSelector(getLanguage);
  const hash = useSelector(getHash);

  const hasAllUserData = Boolean(country && language && hash);

  // Get auth token for chat
  useEffect(() => {
    if (!enabled || !hasAllUserData) {
      return;
    }
    (async () => {
      try {
        const res = await driverRegistrationApiClient.liveChatGetAuthToken({
          hash,
        });
        setChatAuthToken(res.token);
      } catch (error) {
        Logger.log('Live Chat: Failed to get auth token', { error });
      }
    })();
  }, [enabled, hasAllUserData, hash]);

  // Load script once we have auth token
  useEffect(() => {
    if (!chatAuthToken || isScriptLoaded) {
      return;
    }

    // LiveChat stub
    if (!window.liveChat) {
      (window as any).liveChat = (...args: unknown[]) => {
        (window.liveChat!.q = window.liveChat!.q || []).push(args);
      };
      window.liveChat!.t = Date.now();
    }

    // Inject script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://livechat.infobip.com/widget.js';
    script.onload = async () => {
      try {
        await liveChatCommand(LiveChatAction.SET_FONT, {
          fontFaceUrl: 'https://fonts.googleapis.com/css2?family=Inter',
          fontFamily: 'Inter',
        });
        await liveChatCommand(LiveChatAction.CLEAR, key);
        await liveChatCommand(LiveChatAction.INIT, key);
        setIsScriptLoaded(true);
      } catch (error) {
        Logger.log('Live Chat: Initialization error', { error });
      }
    };
    document.head.appendChild(script);
  }, [chatAuthToken, isScriptLoaded, key]);

  // After script is loaded - authenticate user
  useEffect(() => {
    if (isScriptLoaded && chatAuthToken) {
      if (isUserAuthenticated === AuthState.NOT_AUTHENTICATED) {
        (async () => {
          setIsUserAuthenticated(AuthState.IN_PROGRESS);
          await liveChatCommand(LiveChatAction.AUTH, chatAuthToken);
          setIsUserAuthenticated(AuthState.AUTHENTICATED);
        })();
      }
    }
  }, [isScriptLoaded, chatAuthToken, isUserAuthenticated]);

  // Send contextual data - after auth and on language change
  useEffect(() => {
    if (isUserAuthenticated !== AuthState.AUTHENTICATED) {
      return;
    }

    const locale = `${language}-${country?.toUpperCase()}`;
    liveChatCommand(LiveChatAction.SET_LANGUAGE, locale);

    // Send contextual data is queued and executed only when conversation is started
    // See https://www.infobip.com/docs/live-chat/javascript-sdk-in-live-chat#send-contextual-data
    liveChatCommand(LiveChatAction.SEND_CONTEXTUAL_DATA, {
      metadata: { country, language },
      multiThreadStrategy: 'ALL_PLUS_NEW',
    });
  }, [isUserAuthenticated, country, language]);

  // Setup MixPanel hooks
  useEffect(() => {
    if (isScriptLoaded && mixpanel) {
      try {
        setupChatHooks(mixpanel);
      } catch (error) {
        Logger.log('Live Chat: Setup hooks error', { error });
      }
    }
  }, [isScriptLoaded, mixpanel]);
};
